import { Checkbox, Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { ButtonWrapper, RanaTable, TABLE_SELECTION_COLUMN_WIDTH } from "@nens/ranalib";
import { displayBytes } from "@nens/shared-utils";

import { Profile } from "@/lib/profile";
import { RanaFileIcon } from "@/lib/rana-file-data-type";
import { getPrettyDate } from "@/utils/get-pretty-time";

import { CompleteFile } from "../../types/file-reference-with-file";
import { getFileLayers, getFileName } from "../../utils/files";
import { PublicationDetailFileAdd } from "../detail/components/file-add";
import { SelectedFileOrLayer } from "../map-layers";

import { useFileLayerSelection } from "./hooks/use-file-layer-selection";
import { useFilesTable } from "./hooks/use-files-table";

type AllLayerRowProps = {
  layer: "all";
  isSelected: boolean;
  onCheckboxChange: (checked: boolean) => void;
};

type OneLayerRowProps = {
  layer: SelectedFileOrLayer;
  isSelected: boolean;
  onCheckboxChange: (
    checked: boolean,
    file: CompleteFile,
    name: string,
    layerInFile: string | undefined
  ) => void;
};

type LayerRowProps = OneLayerRowProps | AllLayerRowProps;

const LayerRow = ({ layer, isSelected, onCheckboxChange }: LayerRowProps) => {
  const intl = useIntl();
  const isAll = layer === "all";
  const title = isAll
    ? intl.formatMessage({
        id: "publication.map.layermodal.all_layers",
        defaultMessage: "All Layers",
      })
    : layer.name;
  return (
    <RanaTable.Row.Accordion.Panel
      title={title}
      {...(isAll
        ? { rowId: "all", onClick: () => onCheckboxChange(!isSelected) }
        : {
            rowId: layer.name,
            onClick: () => onCheckboxChange(!isSelected, layer.file, layer.name, layer.layerInFile),
          })}
    >
      <Flex w={TABLE_SELECTION_COLUMN_WIDTH}>
        <Checkbox pointerEvents="none" flexShrink={0} isChecked={isSelected} />
      </Flex>

      <Flex fontStyle={isAll ? "italic" : undefined} isTruncated>
        {title}
      </Flex>
    </RanaTable.Row.Accordion.Panel>
  );
};

interface FileRowProps {
  file: CompleteFile;
  disabledReason: string | undefined; // When set the file cannot be selected, the reason is shown as title
  onLayerCheckboxChange: (
    checked: boolean,
    file: CompleteFile,
    name: string,
    layerInFile: string | undefined
  ) => void;
  isLayerSelected: (fileId: string, layerInFile: string | undefined) => boolean;
  handleAllLayerCheckbox: (checked: boolean, file: CompleteFile) => void;
}

const FileRow = ({
  file,
  disabledReason,
  onLayerCheckboxChange,
  isLayerSelected,
  handleAllLayerCheckbox,
}: FileRowProps) => {
  const fileData = file.file;
  const fileName = getFileName(file);
  const fileLayers = getFileLayers(file);
  const fileModifiedDate = getPrettyDate(fileData.lastModified);
  const isDisabled = !!disabledReason;
  const defaultIsOpen = !isDisabled && fileLayers.length === 1; // Auto-expand single-layer files

  if (!isDisabled && !fileLayers.length) {
    return null;
  }

  return (
    <RanaTable.Row.Accordion defaultIsOpen={defaultIsOpen} key={file.path}>
      <RanaTable.Row.Accordion.Button
        rowId={file.path}
        isDisabled={isDisabled}
        title={disabledReason}
      >
        <RanaTable.Cell>
          <RanaFileIcon fileOrDirectory={fileData} />
          {fileName}
        </RanaTable.Cell>
        <RanaTable.Cell>{displayBytes(fileData.size)}</RanaTable.Cell>
        <RanaTable.Cell title={file.ref}>{file.ref?.slice(0, 7)}</RanaTable.Cell>
        <RanaTable.Cell>
          <Profile inTable user={fileData.user} />
        </RanaTable.Cell>
        <RanaTable.Cell>{fileModifiedDate}</RanaTable.Cell>
      </RanaTable.Row.Accordion.Button>
      {!isDisabled && fileLayers.length > 1 && (
        <LayerRow
          layer="all"
          isSelected={fileLayers.every(({ layerInFile }) =>
            isLayerSelected(file.path, layerInFile)
          )}
          onCheckboxChange={(checked) => {
            handleAllLayerCheckbox(checked, file);
          }}
        />
      )}
      {!isDisabled &&
        fileLayers.map((layer) => (
          <LayerRow
            key={layer.file.path + layer.layerInFile}
            layer={layer}
            isSelected={isLayerSelected(file.path, layer.layerInFile)}
            onCheckboxChange={onLayerCheckboxChange}
          />
        ))}
    </RanaTable.Row.Accordion>
  );
};

interface PublicationFilesTableProps {
  selectedItems: SelectedFileOrLayer[];
  onSelectionChange: (items: SelectedFileOrLayer[]) => void;
  mode?: "layers" | "study-area";
}

export const PublicationFilesTable = ({
  selectedItems,
  onSelectionChange,
  mode = "layers",
}: PublicationFilesTableProps) => {
  const intl = useIntl();
  const {
    filteredPublicationFiles,
    emptyMessage,
    columnDefinitions,
    searchString,
    setSearchString,
    hideDisabled,
    setHideDisabled,
  } = useFilesTable({ mode });

  const { handleLayerCheckbox, handleAllLayerCheckbox, isLayerSelected } = useFileLayerSelection({
    selectedItems,
    onSelectionChange,
  });

  return (
    <RanaTable
      columnDefinitions={columnDefinitions}
      tableDataIds={filteredPublicationFiles.map(({ file }) => file.path)}
      emptyElement={<RanaTable.Empty>{emptyMessage}</RanaTable.Empty>}
    >
      <ButtonWrapper>
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "publication.map.layermodal.search",
              defaultMessage: "Find your file",
            }),
            searchString,
            setSearchString,
          }}
          checkboxes={[
            {
              label: intl.formatMessage({
                id: "publication.map.layermodal.hide_disabled",
                defaultMessage: "Hide unavailable files",
              }),
              checked: hideDisabled,
              setChecked: setHideDisabled,
            },
          ]}
        />
        <PublicationDetailFileAdd />
      </ButtonWrapper>

      <RanaTable.Container sx={{ minHeight: "unset" }}>
        <RanaTable.Header />
        <RanaTable.Body>
          {filteredPublicationFiles.map(({ file, disabledReason }) => (
            <FileRow
              key={file.path}
              file={file}
              disabledReason={disabledReason}
              onLayerCheckboxChange={handleLayerCheckbox}
              isLayerSelected={isLayerSelected}
              handleAllLayerCheckbox={handleAllLayerCheckbox}
            />
          ))}
        </RanaTable.Body>
      </RanaTable.Container>
    </RanaTable>
  );
};
